import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../config/firebase";
import "../css/Auth.css";

const INTER_FONT = "'Inter', Arial, Helvetica, sans-serif";
const EMAIL_RE   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Maps Cloud Function HttpsError codes to user-facing messages.
// The backend intentionally returns success for auth/user-not-found so the
// frontend never learns whether an account exists — only server-side failures
// and configuration problems surface as errors here.
function mapResetError(err) {
  const code = err?.code || "";
  const msg  = err?.message || "";

  if (code === "functions/invalid-argument") {
    return "Please enter a valid email address.";
  }
  if (code === "functions/too-many-requests") {
    return "Too many requests. Please wait a few minutes and try again.";
  }
  if (code === "functions/internal") {
    return "Failed to send the reset email. Please try again later.";
  }
  if (code === "functions/unavailable" ||
      msg.toLowerCase().includes("network") ||
      msg.toLowerCase().includes("offline")) {
    return "Network error. Please check your connection and try again.";
  }
  return "Something went wrong. Please try again.";
}

export default function ForgotPassword() {
  const navigate  = useNavigate();
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState("");

  const handleEmailChange = (e) => {
    if (error) setError("");
    setEmail(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const trimmed = email.trim();
    if (!trimmed) {
      setError("Please enter your email address.");
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const sendReset = httpsCallable(functions, "sendBrandedPasswordReset");
      await sendReset({ email: trimmed });
      setSent(true);
    } catch (err) {
      console.error("[ForgotPassword] reset error code:", err?.code);
      setError(mapResetError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="auth-container"
      style={{
        fontFamily: INTER_FONT,
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      <video
        className="background-video"
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: -1,
        }}
      >
        <source src="/videos/sunsetbluedark.mp4" type="video/mp4" />
      </video>

      <form
        className="auth-form"
        onSubmit={handleSubmit}
        style={{ fontFamily: INTER_FONT, zIndex: 1 }}
      >
        <h2 style={{ fontFamily: INTER_FONT, fontWeight: 700 }}>Reset Password</h2>

        {sent ? (
          <>
            <p
              className="auth-msg auth-msg--success"
              role="status"
              aria-live="polite"
            >
              If an account exists for this email, a password reset link has been sent.
              Please check your inbox (and spam folder).
            </p>
            <button
              type="button"
              onClick={() => navigate("/sign-in")}
              style={{ fontFamily: INTER_FONT, fontWeight: 700, marginTop: 16 }}
            >
              Back to Sign In
            </button>
          </>
        ) : (
          <>
            <p style={{ color: "rgba(255,255,255,0.75)", marginBottom: 4, fontSize: "0.9rem" }}>
              Enter your account email and we'll send you a link to reset your password.
            </p>

            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={handleEmailChange}
              required
              autoComplete="email"
              style={{ fontFamily: INTER_FONT }}
            />

            {error && (
              <p className="auth-msg auth-msg--error" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className={loading ? "loading" : ""}
              style={{ fontFamily: INTER_FONT, fontWeight: 700 }}
            >
              {loading ? "Sending..." : "Send Reset Email"}
            </button>

            <p className="switch-link" style={{ fontFamily: INTER_FONT }}>
              Remember your password?{" "}
              <span
                onClick={() => navigate("/sign-in")}
                style={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Sign In
              </span>
            </p>
          </>
        )}
      </form>
    </div>
  );
}
